import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PricingTable } from "@/components/Pricing";

function PageHead({ eyebrow, title, sub }) {
  return (
    <div className="max-w-3xl">
      <p className="text-xs uppercase tracking-[0.2em] text-[#B87333] font-semibold">{eyebrow}</p>
      <h1 className="mt-3 font-serif text-4xl sm:text-5xl text-[#2C2621] font-semibold leading-tight">{title}</h1>
      {sub && <p className="mt-4 text-lg text-[#635B52] leading-relaxed">{sub}</p>}
    </div>
  );
}

export default function Services() {
  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/addons").then((r) => setAddons(r.data)).catch(() => {});
  }, []);

  const fmtAddon = (a) => {
    if (a.price_type === "custom") return "Custom estimate required";
    if (a.price_type === "starting") return `Starting at $${a.price}`;
    if (a.price_type === "per_load") return `$${a.price} per standard load`;
    if (a.price_type === "each") return `$${a.price} each`;
    return `$${a.price}`;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <PageHead eyebrow="Services & Pricing" title="Simple, transparent pricing"
        sub="Prices reflect properties in routinely maintained condition. Add-ons, unusual conditions, and larger properties may adjust the final total." />

      {services.map((s) => (
        <section key={s.slug} className="mt-16">
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h2 className="font-serif text-2xl sm:text-3xl text-[#2C2621]">{s.name}</h2>
            <Link to={`/${s.slug === "airbnb" ? "airbnb-turnover" : s.slug === "residential" ? "residential" : "deep-cleaning"}`}
              className="text-[#B87333] font-medium text-sm hover:underline">Details →</Link>
          </div>
          <p className="mt-2 text-[#635B52] max-w-3xl">{s.short_description}</p>
          {s.kind === "deep" && (
            <div className="mt-4 bg-[#F5F0E8] border-l-4 border-[#B87333] rounded-r-xl p-4 text-sm text-[#2C2621] max-w-3xl" data-testid="deep-notice-services">
              Deep-cleaning prices are starting prices. Masaya must inspect the property or review and approve current photos before confirming the final price and appointment.
            </div>
          )}
          <div className="mt-6"><PricingTable service={s} /></div>
        </section>
      ))}

      <section className="mt-20">
        <h2 className="font-serif text-2xl sm:text-3xl text-[#2C2621]">Add-ons</h2>
        <p className="mt-2 text-[#635B52]">Customize any cleaning with optional extras.</p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {addons.map((a) => (
            <div key={a.slug} className="bg-white rounded-2xl border border-[#E6DEC3] p-5 shadow-[0_4px_20px_rgba(44,38,33,0.04)]" data-testid={`addon-${a.slug}`}>
              <p className="font-semibold text-[#2C2621]">{a.name}</p>
              <p className="mt-1 text-[#B87333] font-medium">{fmtAddon(a)}</p>
              {a.duration_minutes > 0 && <p className="mt-1 text-xs text-[#635B52]">+{a.duration_minutes} min</p>}
              {a.note && <p className="mt-1 text-xs text-[#635B52]">{a.note}</p>}
            </div>
          ))}
        </div>
      </section>

      <div className="mt-16 bg-[#2C2621] rounded-3xl p-10 text-center">
        <h3 className="font-serif text-3xl text-white">Ready to book?</h3>
        <p className="mt-2 text-[#E6DEC3]">Pick your service and see live availability in minutes.</p>
        <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
          <Link to="/book" className="bg-[#B87333] hover:bg-[#A06127] text-white font-medium rounded-full px-7 py-3 shadow-md transition-all" data-testid="services-book-btn">Book Now</Link>
          <Link to="/get-estimate" className="bg-white text-[#2C2621] font-medium rounded-full px-7 py-3 transition-all" data-testid="services-estimate-btn">Get an Estimate</Link>
        </div>
      </div>
    </div>
  );
}
